import React, { useEffect, useState } from 'react';

import AlertBanner from '~/components/core/AlertBanner';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import InnerTabs from '~/components/core/Layout/InnerTabs/InnerTabs';
import { FnolSubOrgLobFilterBar } from '~/components/Fnol/filters/FnolSubOrgLobFilterBar';
import { useFnolAiInboxStatus } from '~/components/Fnol/FnolAiInbox/hooks/useFnolAiInboxStatus';
import { FnolAiInboxTab } from '~/components/Fnol/FnolAiInbox/tabs/FnolAiInboxTab';
import type { InboxType, OnNewLossCallback } from '~/components/Fnol/FnolAiInbox/types';
import { FNOL_INBOX_TAB_STORAGE_KEY } from '~/components/Fnol/FnolAiInbox/utils';

interface FnolAiInboxContainerProps {
  onNewLoss?: ({ fnolDraft, communicationId, fnolAiRecommendationId, subOrgId, lob }: OnNewLossCallback) => void;
  /** Fired when the user expands the card, so the page can scroll it into view. */
  onExpand?: () => void;
}

export interface InboxFilterState {
  subOrgIds: number[];
  includeUnknownSubOrganization: boolean;
  lobs: string[];
  includeUnknownLob: boolean;
}

const QUEUE_TAB_URL = 'queue';

const EMPTY_FILTERS: InboxFilterState = {
  subOrgIds: [],
  includeUnknownSubOrganization: false,
  lobs: [],
  includeUnknownLob: false,
};

export const FnolAiInboxContainer: React.FC<FnolAiInboxContainerProps> = ({ onNewLoss, onExpand }) => {
  // Use the status hook directly instead of context
  const { inboxStatus, reloadInboxStatus } = useFnolAiInboxStatus({ shouldFetch: true });

  // Manage filters locally
  const [filters, setFilters] = useState<InboxFilterState>(EMPTY_FILTERS);

  // Only an org that renders the inbox seeds the key, so the FNOL links of one that never does keep
  // pointing at the page's own address.
  useEffect(() => {
    if (!sessionStorage.getItem(FNOL_INBOX_TAB_STORAGE_KEY)) {
      sessionStorage.setItem(FNOL_INBOX_TAB_STORAGE_KEY, QUEUE_TAB_URL);
    }
  }, []);

  const onExpandCollapse = (isOpen: boolean) => {
    reloadInboxStatus();
    if (isOpen) {
      onExpand?.();
    }
  };

  const onSubOrgChange = (subOrgIds: number[], includeUnknown: boolean) => {
    setFilters({
      ...filters,
      subOrgIds,
      includeUnknownSubOrganization: includeUnknown,
    });
  };

  const onLobChange = (lobs: string[], includeUnknown: boolean) => {
    setFilters({
      ...filters,
      lobs,
      includeUnknownLob: includeUnknown,
    });
  };

  const onClear = () => {
    setFilters(EMPTY_FILTERS);
  };

  const tabs = [
    {
      key: 'queue_tab',
      label: 'Queue',
      url: QUEUE_TAB_URL,
      component: FnolAiInboxTab,
      props: {
        inboxType: 'in-queue' as InboxType,
        onNewLoss,
        filters, // Pass filters to the tab
      },
    },
    {
      key: 'done_tab',
      label: 'Done',
      url: 'done',
      component: FnolAiInboxTab,
      props: {
        inboxType: 'done' as InboxType,
        filters, // Pass filters to the tab
      },
    },
  ];

  return (
    <CollapsibleWrapper
      title="FNOL Inbox"
      actionCard
      noBorder
      backgroundColor={CollapsibleWrapper.BACKGROUND_COLOR.WHITE}
      onCollapse={onExpandCollapse}
      defaultState={true}
    >
      {inboxStatus?.is_ai_high_demand && (
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.WARNING}
          withIcon
          title="Processing is slower than usual"
          note="There is high demand right now, so new claims may take longer to process."
          className="mb-12"
        />
      )}
      <FnolSubOrgLobFilterBar
        subOrgIds={filters.subOrgIds}
        includeUnknownSubOrganization={filters.includeUnknownSubOrganization}
        lobs={filters.lobs}
        includeUnknownLob={filters.includeUnknownLob}
        onSubOrgChange={onSubOrgChange}
        onLobChange={onLobChange}
        onClear={onClear}
      />
      <InnerTabs
        tabs={tabs}
        renderContainerClassName="bg-white"
        onTabClick={(_event, tab) => sessionStorage.setItem(FNOL_INBOX_TAB_STORAGE_KEY, tab.url)}
      />
    </CollapsibleWrapper>
  );
};
